import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';

interface OfflineAction {
  id: string;
  type: 'create' | 'update' | 'delete';
  endpoint: string;
  data: any;
  timestamp: number;
}

export const useOfflineStorage = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingActions, setPendingActions] = useState<OfflineAction[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncPendingActions();
      toast({
        title: "Connection Restored",
        description: "Syncing your offline changes...",
      });
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast({
        title: "You're Offline",
        description: "Changes will be saved locally and synced when connection is restored.",
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initialize IndexedDB
    initializeDB();
    loadPendingActions();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const initializeDB = () => {
    const request = indexedDB.open('BillPortalDB', 1);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      if (!db.objectStoreNames.contains('pendingActions')) {
        db.createObjectStore('pendingActions', { keyPath: 'id' });
      }
      
      if (!db.objectStoreNames.contains('bills')) {
        db.createObjectStore('bills', { keyPath: 'id' });
      }
      
      if (!db.objectStoreNames.contains('suppliers')) {
        db.createObjectStore('suppliers', { keyPath: 'id' });
      }
      
      if (!db.objectStoreNames.contains('customers')) {
        db.createObjectStore('customers', { keyPath: 'id' });
      }
    };
  };

  const loadPendingActions = async () => {
    try {
      const actions = await getFromIndexedDB('pendingActions');
      setPendingActions(Array.isArray(actions) ? actions : []);
    } catch (error) {
      console.error('Failed to load pending actions:', error);
    }
  };

  const addOfflineAction = async (action: Omit<OfflineAction, 'id' | 'timestamp'>) => {
    const offlineAction: OfflineAction = {
      ...action,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now()
    };

    try {
      await saveToIndexedDB('pendingActions', offlineAction);
      setPendingActions(prev => [...prev, offlineAction]);
      
      toast({
        title: "Action Saved Offline",
        description: "Your changes will be synced when connection is restored.",
      });
    } catch (error) {
      console.error('Failed to save offline action:', error);
      throw error;
    }
  };

  const syncPendingActions = async () => {
    if (pendingActions.length === 0) return;

    for (const action of pendingActions) {
      try {
        const response = await fetch(action.endpoint, {
          method: action.type === 'create' ? 'POST' : 
                 action.type === 'update' ? 'PUT' : 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: action.type !== 'delete' ? JSON.stringify(action.data) : undefined
        });

        if (response.ok) {
          await removeFromIndexedDB('pendingActions', action.id);
          setPendingActions(prev => prev.filter(a => a.id !== action.id));
        }
      } catch (error) {
        console.error('Failed to sync action:', action, error);
      }
    }

    if (pendingActions.length > 0) {
      toast({
        title: "Sync Complete",
        description: "All offline changes have been synchronized.",
      });
    }
  };

  const saveDataOffline = async (storeName: string, data: any) => {
    try {
      await saveToIndexedDB(storeName, data);
    } catch (error) {
      console.error('Failed to save data offline:', error);
      throw error;
    }
  };

  const getDataOffline = async (storeName: string, key?: string) => {
    try {
      return await getFromIndexedDB(storeName, key);
    } catch (error) {
      console.error('Failed to get data offline:', error);
      return null;
    }
  };

  return {
    isOnline,
    pendingActions: pendingActions.length,
    addOfflineAction,
    syncPendingActions,
    saveDataOffline,
    getDataOffline
  };
};

// IndexedDB helper functions
const saveToIndexedDB = (storeName: string, data: any): Promise<void> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('BillPortalDB', 1);
    
    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const addRequest = store.put(data);
      
      addRequest.onsuccess = () => resolve();
      addRequest.onerror = () => reject(addRequest.error);
    };
    
    request.onerror = () => reject(request.error);
  });
};

const getFromIndexedDB = (storeName: string, key?: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('BillPortalDB', 1);
    
    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const getRequest = key ? store.get(key) : store.getAll();
      
      getRequest.onsuccess = () => resolve(getRequest.result);
      getRequest.onerror = () => reject(getRequest.error);
    };
    
    request.onerror = () => reject(request.error);
  });
};

const removeFromIndexedDB = (storeName: string, key: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('BillPortalDB', 1);
    
    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const deleteRequest = store.delete(key);
      
      deleteRequest.onsuccess = () => resolve();
      deleteRequest.onerror = () => reject(deleteRequest.error);
    };
    
    request.onerror = () => reject(request.error);
  });
};